// src/layouts/AgentLayout.tsx
import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { AppUserHeader, AgentSideBar, LiveChatButton, ChatOptions } from '../components';
import { Box, IconButton, TextField, } from "@mui/material";
import { AgentService, AuthService, } from '../services';
import { useDispatch } from 'react-redux';
import { updateUser } from '../stores/authSlice';
import { useLiveChat } from '../hooks/useLiveChat';
import { ChatBotBody, ChatBotBox, ChatBotFooter, ChatBotHeader, ChatCompanyLogo, ChatCompanyName, ChatFooterOptions, ChatMsg, ChatMsgListing, ChatMsgOptions, ChatMssListBox, LiveChatMainBox } from '../pages/Customer/styles/Dashboard.styles';
import { useAuth } from '../hooks/useAuth';
import { useChatbot } from '../hooks/useChatbot';
import { ChatMssList } from '../pages/Public/styles/ChatBot.styled';
import { Option, TableData, Message } from '../types/chatBot';
import { useFileAttachments } from '../hooks/useFileAttachments';
import AttachFileIcon from '@mui/icons-material/AttachFile';


const AgentLayout: React.FC = () => {
  const dispatch = useDispatch();
  const user = useAuth();
  const { isActive, chatMode, setChatMode, chatbtnClick, handleBackToOptions } = useLiveChat();
  const {
    chatBodyRef, companyDetails, setCompanyDetails, formData, setFormData,
    currentKey, setCurrentKey, conversationData, setConversationData,
    messages, setMessages, input, setInput, error, setError
  } = useChatbot();
  const { attachError, setAttachError, attachments, setAttachments, handleFileChange } = useFileAttachments();

  const getUserData = async () => {
    try {
      const resp = await AuthService.getProfile({ add: ['userDetails', 'parent.sendgridUser', 'parent.company', "parent.tenantSetting", 'parent.instagramConfig', 'parent.facebookConfig'] });
      const udpatedResponse = {
        ...resp?.data,
        instagramConfig: resp?.data?.parent?.instagramConfig,
        facebookConfig: resp?.data?.parent?.facebookConfig,
        tenantSetting: resp?.data?.parent?.tenantSetting,
      };
      // console.log(udpatedResponse, "udpatedResponse");
      dispatch(updateUser(udpatedResponse));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    getUserData();
  }, []);
  useEffect(() => {
    if (chatBodyRef.current) {
      const el = chatBodyRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);
  const lastMsgIndex = messages.length - 1;
  const node = conversationData[currentKey];

  const handleChatModeSelect = (mode: 'ai' | 'agent') => {
    setChatMode(mode);
    setCurrentKey("agent");
    const welcomeText = `Hello ${user?.firstName || 'there'}! I'm your virtual assistant. How can I help you today?`;
    setMessages([{
      sender: 'bot',
      text: welcomeText,
    }]);
  };

  const handleSelect = async (opt: Option | TableData) => {
    console.log(opt, "opt..");
    const nextKey = opt.next || '';
    const nextNode = conversationData[nextKey];
    if (opt.action) {
      try {
        await opt.action();
        setCurrentKey(nextKey);
        const botMessage: Message = {
          sender: 'bot',
          text: nextNode?.text ?? '',
          options: nextNode?.options,
        };
        setMessages((prev) => [...prev, botMessage]);
      } catch (err) {
        const fallbackMessage: Message = {
          sender: 'bot',
          text: 'Sorry, I encountered an error processing your selection. Please try again.',
        };
        setMessages((prev) => [...prev, fallbackMessage]);
        return;
      }
    } else {
      setCurrentKey(nextKey);
      const botMessage: Message = {
        sender: 'bot',
        text: nextNode?.text ?? '',
        options: nextNode?.options,
      };
      setMessages((prev) => [...prev, botMessage]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return setError('This field is required');
    setMessages((prev) => [...prev, {
      sender: 'user',
      text: input,
    }]);
    setInput('');
    const res = await AgentService.getChatData({ question: input });
    console.log(res, "res");
    if (res && res?.statusCode === 200) {
      if (res?.data?.escalated) {
        setMessages((prev) => [...prev, {
          sender: 'bot',
          text: res?.data?.answer || '',
          options: res?.data?.options,
          table: res?.table
        }, {
          sender: "bot",
          text: res?.data?.message,
          options: [{
            text: "Yes",
            action: async () => {
              return await AgentService.confirmEscalation({});
            },
            next: "salesThanks"
          }, {
            text: "No",
            next: "thanks"
          }]
        }]);
        return;
      }
      setMessages((prev) => [...prev, {
        sender: 'bot',
        text: res?.data?.answer || '',
        options: res?.data?.options,
        table: res?.table
      }]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  // console.log(messages, "messages");



  return (
    <Box sx={{ display: 'flex', flexFlow: 'column', height: '100vh' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', backgroundColor: '#F0F0F0', height: 1, }}>
        <AgentSideBar />
        <Box sx={{ display: 'block', flex: 1, maxHeight: 1, maxWidth: 'calc(100% - 248px)' }}>
          <AppUserHeader />
          <Box sx={{ display: 'block', flex: 1, px: 3.75, pt: 2.5, pb: 10, maxHeight: 'calc(100% - 75px)', overflow: 'auto' }}>
            {<Outlet />}
          </Box>
        </Box>
      </Box>
      {user?.featureAvailability?.ai_agent && <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 999 }}>
        <LiveChatButton isActive={isActive} onClick={chatbtnClick} agent={true} />
        {!isActive && (
          <LiveChatMainBox>
            {chatMode === 'options' ? (
              <ChatOptions
                onSelectMode={handleChatModeSelect}
                customerName={user?.firstName}
                agent={true}
              />
            ) : (
              <>
                <ChatBotBox>
                  <ChatBotHeader>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <IconButton
                        size="small"
                        onClick={handleBackToOptions}
                        sx={{ mr: 1 }}
                      >
                        <img src="/images/arrow-left.svg" alt="Back" style={{ width: 20, height: 20 }} />
                      </IconButton>
                      <ChatCompanyLogo>
                        <img src={`${companyDetails?.shortLogoUrl || '/images/static/company-logo.png'}`} alt="Logo" />
                      </ChatCompanyLogo>
                      <ChatCompanyName>
                        {chatMode === 'ai' ? 'AI Assistant' : 'Live Agent'}
                      </ChatCompanyName>
                    </Box>
                  </ChatBotHeader>
                  <ChatBotBody ref={chatBodyRef}>
                    <ChatMsgListing>
                      {messages.map((m, i: number) => {
                        const isCurrent = i === lastMsgIndex;
                        return (
                          <ChatMssList key={i} className={m.sender === 'user' ? 'user' : ''}>
                            <ChatCompanyLogo className="userImage">
                              <img
                                src={
                                  m.sender === 'user'
                                    ? '/images/default-user.svg'
                                    : companyDetails?.shortLogoUrl || '/images/static/company-logo.png'
                                }
                                alt={m.sender}
                              />
                            </ChatCompanyLogo>
                            <ChatMssListBox>
                              <ChatMsg>{m.text}</ChatMsg>
                              {m.sender === 'bot' && (
                                <>
                                  {m?.options?.map((o: Option | TableData) =>
                                    <ChatMsgOptions key={o.text} onClick={() => isCurrent && handleSelect(o)}>
                                      {o.text}
                                    </ChatMsgOptions>
                                  )}
                                </>
                              )}
                            </ChatMssListBox>
                          </ChatMssList>
                        )
                      })}
                    </ChatMsgListing>
                  </ChatBotBody>
                  {
                    node?.input && node?.next &&
                    <ChatBotFooter>
                      <form onSubmit={handleSubmit}>
                        <TextField
                          className="chatTextArea"
                          variant="outlined"
                          placeholder="Enter your message"
                          name="userInput"
                          multiline
                          // rows={1}
                          fullWidth
                          size="small"
                          value={input}
                          onChange={handleInputChange}
                          error={Boolean(error || attachError)}
                          helperText={error || attachError}
                          type={node.input}
                        />
                        {node?.attachment && (
                          <ChatFooterOptions>
                            <IconButton component="label" size="small">
                              <AttachFileIcon />
                              <input type="file" multiple hidden onChange={handleFileChange} />
                            </IconButton>
                          </ChatFooterOptions>
                        )}
                        <IconButton
                          size="small"
                          disabled={(!input.trim() && attachments.length === 0) || Boolean(attachError)}
                          type="submit"
                        >
                          <img src="/images/send-icon.svg" alt="Send" />
                        </IconButton>
                      </form>
                    </ChatBotFooter>
                  }
                </ChatBotBox>
              </>
            )
            }
          </LiveChatMainBox>
        )}
      </Box>}

    </Box>
  );
};

export default AgentLayout;
